// src/Components/Modules/HR/LetterGenerator.js
import React, { useState, useEffect, useMemo } from "react";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import Breadcrumb from "../../Partials/Breadcrumb";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Skeleton,
  Alert,
  Stack,
  Avatar,
  TextField,
  MenuItem,
  Divider,
  Fade,
  Zoom,
} from "@mui/material";
import {
  FaFilePdf,
  FaDownload,
  FaFileAlt,
  FaFileContract,
  FaExclamationTriangle,
  FaMoneyBillWave,
  FaAward,
  FaUsers,
  FaEnvelope,
  FaEye,
  FaPrint,
  FaSync,
  FaCheckCircle,
  FaInfoCircle,
  FaUser,
  FaClipboardList,
} from "react-icons/fa";
import { styled } from "@mui/material/styles";
import Swal from "sweetalert2";

// Styled Components
const GradientPaper = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: "0 10px 40px rgba(102, 126, 234, 0.3)",
}));

const StatCard = styled(Card)(({ theme }) => ({
  background: "white",
  borderRadius: theme.spacing(3),
  padding: theme.spacing(3),
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.3s ease",
  border: "1px solid rgba(0,0,0,0.08)",
  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
  },
}));

const LetterTypeCard = styled(Card)(({ theme, selected }) => ({
  borderRadius: theme.spacing(2),
  border: selected ? "2px solid #667eea" : "2px solid transparent",
  background: selected ? "linear-gradient(135deg, #f5f7ff 0%, #e8ecff 100%)" : "white",
  transition: "all 0.3s ease",
  cursor: "pointer",
  position: "relative",
  overflow: "hidden",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 25px rgba(102, 126, 234, 0.2)",
    borderColor: "#667eea",
  },
  "&::before": selected && {
    content: '"✓"',
    position: "absolute",
    top: 8,
    right: 8,
    width: 24,
    height: 24,
    borderRadius: "50%",
    background: "#667eea",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    fontWeight: "bold",
  },
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  borderRadius: 2,
  textTransform: "none",
  fontWeight: 600,
  padding: theme.spacing(1.5, 4),
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  "&:hover": {
    background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
    boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
  },
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  borderRadius: 2,
  textTransform: "none",
  fontWeight: 600,
  padding: theme.spacing(1.5, 4),
  background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
  color: "#1b5e20",
  "&:hover": {
    background: "linear-gradient(135deg, #38f9d7 0%, #43e97b 100%)",
    boxShadow: "0 6px 20px rgba(67, 233, 123, 0.4)",
  },
}));

const letterTypes = [
  {
    value: "offer",
    label: "Offer Letter",
    icon: FaFileContract,
    color: "#1976d2",
    bgColor: "#e3f2fd",
    description: "Employment offer letter for new hires",
  },
  {
    value: "appointment",
    label: "Appointment Letter",
    icon: FaFileAlt,
    color: "#7b1fa2",
    bgColor: "#f3e5f5",
    description: "Official appointment confirmation",
  },
  {
    value: "warning",
    label: "Warning Letter",
    icon: FaExclamationTriangle,
    color: "#f57c00",
    bgColor: "#fff3e0",
    description: "Disciplinary warning notice",
  },
  {
    value: "salary_certificate",
    label: "Salary Certificate",
    icon: FaMoneyBillWave,
    color: "#2e7d32",
    bgColor: "#e8f5e9",
    description: "Proof of income certificate",
  },
  {
    value: "experience",
    label: "Experience Letter",
    icon: FaAward,
    color: "#c2185b",
    bgColor: "#fce4ec",
    description: "Employment experience certificate",
  },
];

const LetterGenerator = () => {
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ type: "", employee_id: "" });
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState(null);
  const [generated, setGenerated] = useState(false);

  useEffect(() => {
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    setLoadingData(true);
    try {
      const res = await AxiosAuthInstance.get("add_employee");
      setEmployees(
        Array.isArray(res.data.employee) ? res.data.employee : []
      );
    } catch (err) {
      setError("Failed to load employees");
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load employee data. Please try again.",
      });
    } finally {
      setLoadingData(false);
    }
  };

  const validateForm = () => {
    if (!form.employee_id) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please select an employee",
      });
      return false;
    }
    if (!form.type) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please select a letter type",
      });
      return false;
    }
    return true;
  };

  const generate = async (download = false) => {
  if (!validateForm()) return;
  
  const selectedEmployee = employees.find((e) => e.id === form.employee_id);
  const selectedType = letterTypes.find((t) => t.value === form.type);
  
  const result = await Swal.fire({
    title: download ? "Download Letter?" : "Generate Letter?",
    html: `
      <div style="text-align: left; padding: 10px;">
        <p><strong>Employee:</strong> ${selectedEmployee?.employee_name || "N/A"}</p>
        <p><strong>Letter Type:</strong> ${selectedType?.label || "N/A"}</p>
        <p style="color: #757575; font-size: 14px;">
          ${download ? "The PDF will be downloaded automatically." : "The letter will be generated and ready for download."}
        </p>
      </div>
    `,
    icon: "question",
    showCancelButton: true,
    confirmButtonColor: download ? "#43e97b" : "#667eea",
    cancelButtonColor: "#757575",
    confirmButtonText: download ? "Yes, Download" : "Yes, Generate",
    cancelButtonText: "Cancel",
  });
  
  if (result.isConfirmed) {
    setLoading(true);
    try {
      const res = await AxiosAuthInstance.post("hr/letters/generate", form);
      
      if (download) {
        window.open(
          `${process.env.REACT_APP_API_BASE_URL}/api/v1/letters/download/${form.type}/${form.employee_id}`
        );
        
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Letter downloaded successfully",
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        setGenerated(true);
        Swal.fire({
          icon: "success",
          title: "Generated!",
          text: "Letter generated successfully. Click Download for PDF.",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Failed to generate letter";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMsg,
      });
    } finally {
      setLoading(false);
    }
  }
};

  const selectedLetterType = letterTypes.find((t) => t.value === form.type);
  const selectedEmployee = employees.find((e) => e.id === form.employee_id);

  // Calculate stats
  const stats = useMemo(() => {
    return {
      totalEmployees: employees.length,
      totalTypes: letterTypes.length,
    };
  }, [employees]);

  const getInitials = (name) => {
    if (!name) return "N/A";
    const names = name.split(" ");
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getColorFromName = (name) => {
    const colors = [
      "#667eea", "#764ba2", "#f093fb", "#4facfe",
      "#43e97b", "#fa709a", "#fee140", "#30cfd0",
    ];
    if (!name) return colors[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  if (loadingData) {
    return (
      <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
        <Breadcrumb title="Letter Generator" />
        <GradientPaper>
          <Skeleton variant="text" width="60%" height={60} />
          <Skeleton variant="text" width="40%" />
        </GradientPaper>
        <Grid container spacing={3}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      <Breadcrumb title="Letter Generator" />

      {/* Header */}
      <GradientPaper elevation={0}>
        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "center", sm: "space-between" },
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            textAlign: { xs: "center", sm: "left" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "center", sm: "flex-start" },
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            <Typography variant="h4" fontWeight={700} gutterBottom>
              <FaEnvelope style={{ marginRight: 10, verticalAlign: "middle" }} />
              Letter Generator
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Generate and download HR letters instantly
            </Typography>
          </Box>
          <Tooltip title="Refresh Data">
            <IconButton
              onClick={loadEmployees}
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                color: "white",
                "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
              }}
            >
              <FaSync />
            </IconButton>
          </Tooltip>
        </Box>
      </GradientPaper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard elevation={0}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                bgcolor: "#e3f2fd",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <FaUsers size={28} color="#2196f3" />
            </Box>
            <Typography variant="h3" fontWeight={700} color="#1565c0" sx={{ mb: 0.5 }}>
              {stats.totalEmployees}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Total Employees
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard elevation={0}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                bgcolor: "#f3e5f5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <FaClipboardList size={28} color="#7b1fa2" />
            </Box>
            <Typography variant="h3" fontWeight={700} color="#7b1fa2" sx={{ mb: 0.5 }}>
              {stats.totalTypes}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Letter Types
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard elevation={0}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                bgcolor: "#e8f5e9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <FaFilePdf size={28} color="#4caf50" />
            </Box>
            <Typography variant="h3" fontWeight={700} color="#2e7d32" sx={{ mb: 0.5 }}>
              PDF
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Export Format
            </Typography>
          </StatCard>
        </Grid>
      </Grid>

      {/* Letter Type Selection */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          <FaFileAlt style={{ marginRight: 8, verticalAlign: "middle" }} />
          Select Letter Type
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        <Grid container spacing={2}>
          {letterTypes.map((type) => {
            const IconComponent = type.icon;
            return (
              <Grid item xs={12} sm={6} md={4} lg={2.4} key={type.value}>
                <LetterTypeCard
                  selected={form.type === type.value}
                  elevation={0}
                  onClick={() => {
                    setForm({ ...form, type: type.value });
                    setGenerated(false);
                  }}
                >
                  <CardContent sx={{ textAlign: "center", py: 3 }}>
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        bgcolor: type.bgColor,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 2,
                      }}
                    >
                      <IconComponent size={28} color={type.color} />
                    </Box>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      {type.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                      {type.description}
                    </Typography>
                  </CardContent>
                </LetterTypeCard>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* Employee Selection & Actions */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          <FaUser style={{ marginRight: 8, verticalAlign: "middle" }} />
          Select Employee
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Select Employee"
              value={form.employee_id}
              onChange={(e) => {
                setForm({ ...form, employee_id: e.target.value });
                setGenerated(false);
              }}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            >
              {employees.map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar
                      sx={{
                        bgcolor: getColorFromName(emp.employee_name),
                        width: 32,
                        height: 32,
                        fontSize: "0.875rem",
                        fontWeight: 600,
                      }}
                    >
                      {getInitials(emp.employee_name)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {emp.employee_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {emp.employee_designation || "Employee"}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <PrimaryButton
                fullWidth
                variant="contained"
                onClick={() => generate(false)}
                disabled={loading}
                startIcon={loading ? null : <FaFileAlt />}
                size="large"
              >
                {loading ? "Generating..." : "Generate"}
              </PrimaryButton>
              <SecondaryButton
                fullWidth
                variant="contained"
                onClick={() => generate(true)}
                disabled={loading || !generated}
                startIcon={<FaDownload />}
                size="large"
              >
                Download PDF
              </SecondaryButton>
            </Stack>
          </Grid>
        </Grid>

        {/* Selected Info */}
        {(selectedEmployee || selectedLetterType) && (
          <Box sx={{ mt: 3, p: 2, bgcolor: "#f5f7fa", borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              <FaInfoCircle style={{ marginRight: 6, verticalAlign: "middle" }} />
              Selected Details
            </Typography>
            <Grid container spacing={2}>
              {selectedEmployee && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar
                      sx={{
                        bgcolor: getColorFromName(selectedEmployee.employee_name),
                        width: 40,
                        height: 40,
                        fontWeight: 600,
                      }}
                    >
                      {getInitials(selectedEmployee.employee_name)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {selectedEmployee.employee_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {selectedEmployee.employee_designation || "Employee"}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              {selectedLetterType && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        bgcolor: selectedLetterType.bgColor,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {React.createElement(selectedLetterType.icon, {
                        size: 20,
                        color: selectedLetterType.color,
                      })}
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {selectedLetterType.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {selectedLetterType.description}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {/* Info Alert */}
        <Alert severity="info" icon={<FaInfoCircle />} sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>How it works:</strong> Select a letter type, choose an employee, then click "Generate" to create the letter. Once generated, click "Download PDF" to get the document.
          </Typography>
        </Alert>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default LetterGenerator;